/**
 * BE-186 / BE-201 — metric / imperial is the account's `units` (`profile-data.user.units`, default
 * `metric`), written with `PATCH /user/profile {units}`: the same on every device, no browser storage.
 * Behind `profile_locale`: off → the choice lives for the page only.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { patchUserProfile } from '@/features/auth/api/profile'
import { hasSurveysApi } from '@/features/survey/flags'
import { queryCache } from '@/queries'
import type { UserUnits } from '@/types/user'

export function toUserUnits(value: string | null | undefined): UserUnits {
  return value === 'imperial' ? 'imperial' : 'metric'
}

export interface UseAccountUnits {
  /** What the page shows: the local pick of this page, else the account's. */
  units: ComputedRef<UserUnits>
  setUnits: (next: UserUnits) => void
  /** Forget the local pick (a quiz re-opened reads the account again). */
  reset: () => void
}

export function useAccountUnits(accountUnits: Ref<string | null | undefined>): UseAccountUnits {
  const picked = ref<UserUnits | null>(null)
  const units = computed<UserUnits>(() => picked.value ?? toUserUnits(accountUnits.value))

  function setUnits(next: UserUnits) {
    if (next === units.value) return
    picked.value = next
    if (!hasSurveysApi('profile_locale')) return
    patchUserProfile({ units: next })
      .then(() => queryCache.invalidate((key) => Array.isArray(key) && key[0] === 'user-profile'))
      .catch((error) => console.warn('Units: not saved to the account', error))
  }

  function reset() {
    picked.value = null
  }

  return { units, setUnits, reset }
}
